import type { Experiment } from 'types';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { config } from 'config';
import { blocksOf, coverageOf, readingsOf, returnVisitReadingsOf } from './segment-rows';

/**
 * The experiment's exploratory segments, from the visitor-id tables.
 *
 * A live query like the timeseries, fetched apart from the stats blob: the section is the
 * last on the page, and the first paint should not wait for it. `null` from the server
 * means the website never had the visitor ID flag on, and the section does not render.
 *
 * Shapes mirror src/servers/api/statistics/segments/assemble.ts.
 */
export type SegmentCounts = {
  variantId: number;
  enrolled: number;
  converters: number;
  /** Minor units, in the requested currency. */
  revenue: number;
};

export type SegmentsResponse = {
  experimentId: number;
  currency: string;
  event: string;
  config: { graceSeconds: number; lookbackDays: number; returnWindowDays: number; visitGapMinutes: number };
  idSince: string | null;
  historySince: string | null;
  coverage: {
    variants: { variantId: number; counted: number; withId: number; conflicted: number }[];
    events: { total: number; withId: number };
  };
  overall: SegmentCounts[];
  byDevice: { segment: string; rows: SegmentCounts[] }[];
  byVisitorType: { segment: string; rows: SegmentCounts[] }[];
  returnVisits: { variantId: number; matured: number; returnVisits: number; withReturn: number }[];
  variants: { id: number; name: string; isControl: boolean }[];
};

export type SegmentDimension = 'device' | 'visitorType';

type UseExperimentSegmentsParams = { experiment: Experiment; currency: string | null };

export const useExperimentSegments = ({ experiment, currency }: UseExperimentSegmentsParams) => {
  const statisticsClient = useClient(config.STATISTICS_URL);
  const [dimension, setDimension] = useState<SegmentDimension>('device');

  const { data, isLoading, error } = useQuery<SegmentsResponse | null>({
    queryKey: ['EXPERIMENT_SEGMENTS', experiment.id, currency],
    queryFn: () => statisticsClient.get(`/${experiment.id}/segments?currency=${currency ?? 'nill'}`),
    enabled: !!experiment?.id && !!currency,
    // Per-visitor numbers move slowly, and the query is the heaviest on the page.
    staleTime: 1000 * 60 * 5,
  });

  const variants = data?.variants ?? [];
  const overall = data?.overall ?? [];

  return {
    data: data ?? null,
    isLoading,
    error,
    dimension,
    setDimension,
    coverage: data ? coverageOf(data.coverage) : null,
    overall: readingsOf({ rows: overall, variants }),
    blocks: data
      ? blocksOf({
          segments: dimension === 'device' ? data.byDevice : data.byVisitorType,
          overall,
          variants,
          checkSplit: dimension === 'device',
        })
      : [],
    returnVisits: returnVisitReadingsOf({ rows: data?.returnVisits ?? [], variants }),
  };
};
